import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import { findObjectById } from '@/components/Canvas/utils/groupHelpers';

export default {
  data() {
    return {
      imageBWidth:             1,
      objectShadowAngle:       45,
      objectShadowDistance:    0.04,
      objectBlurEdgesDistance: 0.5,
      objectShadowBlur:        0.1,
      tempShapeOutlineColor:   '#000',
      tempShapeShadowColor:    '#846C63',
      tempImageOutlineColor:   '#000',
      tempImageShadowColor:    '#846C63',
      imageEffectColorPopover: { top: 0, left: 0 },
    };
  },
  computed: {
    imageOutlineRows() {
      return [{
        key:     'thickness',
        label:   'Thickness',
        value:   this.imageBWidth,
        min:     0,
        max:     10,
        step:    1,
        fill:    Number(this.imageBWidth) * 10,
        display: this.imageBWidth,
      }];
    },
    imageShadowRows() {
      return [
        {
          key:     'angle',
          label:   'Direction',
          value:   this.objectShadowAngle,
          min:     -180,
          max:     180,
          step:    1,
          fill:    (Number(this.objectShadowAngle) + 180) / 3.6,
          display: `${this.objectShadowAngle}°`,
        },
        {
          key:     'distance',
          label:   'Distance',
          value:   this.objectShadowDistance,
          min:     0,
          max:     2,
          step:    0.01,
          fill:    Math.round(Number(this.objectShadowDistance) * 50),
          display: Math.round(Number(this.objectShadowDistance) * 50),
        },
        {
          key:     'blur',
          label:   'Blur',
          value:   this.objectShadowBlur,
          min:     0,
          max:     2,
          step:    0.01,
          fill:    Number(this.objectShadowBlur) * 50,
          display: Math.round(Number(this.objectShadowBlur) * 50),
        },
      ];
    },
  },
  watch: {
    // Reads the effect values of the selected object into the sliders / swatches above.
    // ImagePanel adds its own watcher for imageEffectSelection.
    activeObject: {
      handler(newVal) {
        const panelObj               = this.effectPanelObject(newVal);
        this.imageBWidth             = (panelObj && panelObj.outline && panelObj.outline.distance) || 5;
        this.objectShadowAngle       = ((panelObj && panelObj.shadow) && panelObj.shadow.angle) || 0;
        this.objectShadowDistance    = ((panelObj && panelObj.shadow) && panelObj.shadow.distance) || 0;
        const blurEdgesDistance      = (panelObj && panelObj.blurEdges && typeof panelObj.blurEdges.distance !== 'undefined')
            ? panelObj.blurEdges.distance
            : 6;
        this.objectBlurEdgesDistance = Math.min(100, Math.max(0, Math.round(parseFloat(blurEdgesDistance) || 0)));
        this.objectShadowBlur        = panelObj && panelObj.shadow && panelObj.shadow.blur ? panelObj.shadow.blur : 0;
        if ((panelObj && panelObj.type === 'shape' && panelObj.StrokeColor)
            || (panelObj && panelObj.type === 'image' && panelObj.ImageType === 'SVGImage' && panelObj.SvgProperty && panelObj.SvgProperty.StrokeColor)
            || (panelObj && panelObj.type === 'image' && panelObj.ImageType.indexOf('FDICText') !== -1 && panelObj.SvgProperty && panelObj.SvgProperty.StrokeColor)
            || (panelObj && panelObj.type === 'image' && panelObj.ImageType.indexOf('FDICLogo') !== -1 && panelObj.SvgProperty && panelObj.SvgProperty.StrokeColor)) {
          this.tempShapeOutlineColor = panelObj.StrokeColor
              || (panelObj.ImageType === 'SVGImage' && panelObj.SvgProperty && panelObj.SvgProperty.StrokeColor)
              || (panelObj.ImageType.indexOf('FDICText') !== -1 && panelObj.SvgProperty && panelObj.SvgProperty.StrokeColor)
              || (panelObj.ImageType.indexOf('FDICLogo') !== -1 && panelObj.SvgProperty && panelObj.SvgProperty.StrokeColor)
              || '#000';
        }
        if (panelObj && panelObj.type === 'shape') {
          this.tempShapeShadowColor = panelObj.shadow && panelObj.shadow.color ? panelObj.shadow.color : '#846C63';
        }
        if (panelObj && panelObj.type === 'image' && (panelObj.ImageType === 'Image' || panelObj.ImageType === 'WhiteboxLogo') && panelObj.ImageType !== 'SVGImage' && panelObj.ImageType.indexOf('FDICText') === -1 && panelObj.ImageType.indexOf('FDICLogo') === -1) {
          this.tempImageOutlineColor = panelObj.outline && panelObj.outline.color ? panelObj.outline.color : '#000';
          this.tempImageShadowColor  = panelObj.shadow && panelObj.shadow.color ? panelObj.shadow.color : '#846C63';
        }
        if (panelObj && panelObj.type === 'image' && (panelObj.ImageType === 'SVGImage' || panelObj.ImageType.indexOf('FDICText') !== -1 || panelObj.ImageType.indexOf('FDICLogo') !== -1)) {
          this.tempShapeShadowColor = panelObj.shadow && panelObj.shadow.color ? panelObj.shadow.color : '#846C63';
        }
      },
      immediate: true,
      deep:      true,
    },
  },
  methods: {
    // The object whose effect values the panel shows: for a group, the first child with an effect.
    effectPanelObject(newVal) {
      const list   = this.getSelectedObjectsList;
      let panelObj = newVal;
      if (newVal && newVal.type === 'group' && list && list.length) {
        const withEffect = list.find(obj => obj
            && obj.effect
            && obj.effect !== 'none'
            && obj.effect !== '');
        panelObj         = withEffect || list[0];
      } else if (newVal && newVal.type !== 'group') {
        panelObj = findObjectById(this.contentList, newVal.Id) || newVal;
      }
      return panelObj;
    },
    removeImageEffect() {
      this.getSelectedObjectsList.forEach((object) => {
        if (object.type === 'image' || object.ImageType === 'SVGImage' || object.ImageType === 'WhiteboxLogo') {
          this.removeImageEffectById(object.Id);
        }
      });
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange();
      this.emitUndoRedoRequest('removeImageEffect');
    },
    removeImageEffectById(id) {
      const selectedObject  = findObjectById(this.contentList, id);
      selectedObject.effect = 'none';

      if (!selectedObject.outline) {
        selectedObject.outline = {};
      }
      if (!selectedObject.shadow) {
        selectedObject.shadow = {};
      }
      selectedObject.outline.distance = 0;
      selectedObject.shadow.color     = '#846C63';
      this.tempImageShadowColor       = '#846C63';
      selectedObject.borderColor      = '#000';
      this.tempImageOutlineColor      = '#000';
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      console.log('removeImageEffectById');
    },
    applyOutlineOnAddress() {
      this.getSelectedObjectsList.forEach((object) => {
        if (object.type === 'image') {
          this.applyOutlineOnImageById(object.Id);
        }
      });
      this.emitUndoRedoRequest('Address Outline');
      setTimeout(() => {
        EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      }, 100);
      setTimeout(() => {
        this.restoreSelectionAfterChange();
      }, 200);
      this.emitUndoRedoRequest('Apply Outline on Text');
    },
    applyOutlineOnImage() {
      this.getSelectedObjectsList.forEach((object) => {
        if (object.type === 'image') {
          this.applyOutlineOnImageById(object.Id);
        }
      });
      this.emitUndoRedoRequest('Image Outline');
      setTimeout(() => {
        EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      }, 100);
      setTimeout(() => {
        this.restoreSelectionAfterChange();
      }, 200);
    },
    applyOutlineOnImageById(id) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject) {
        return;
      } // Ensure selectedObject exists

      let initialDistance = 5; // Default distance for outline effect
      const color         = '#000';

      const outline = {
        color,
        distance: initialDistance,
      };

      if (selectedObject.type == 'shape' && selectedObject.shapeType == 'polygon' && selectedObject.effect && selectedObject.effect == 'outline') {
        selectedObject.X         += 1;
        selectedObject.Y         += 1;
        selectedObject.svgScaleX -= 0.005;
        selectedObject.svgScaleY -= 0.005;
      }

      selectedObject.effect = 'outline';

      if (selectedObject.type == 'shape') {
        selectedObject.StrokeWidth = 0;
      } else if (selectedObject.SvgProperty && selectedObject.ImageType === 'SVGImage') {
        selectedObject.SvgProperty.StrokeWidth = 0;
        EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_STROKE_COLOR_BY_ID, selectedObject.SvgProperty.StrokeColor, selectedObject.Id);
      }
      setTimeout(() => {
        if (selectedObject.type == 'image' && selectedObject.ImageType == 'WhiteboxLogo') {
          console.log('WhiteboxLogo object');
          initialDistance /= 5;
        }
        const elem = document.querySelector(`.canvas-main-html-container [id='${selectedObject.Id}']`);
        if (elem) {
          const filterValue = `drop-shadow(${initialDistance}px 0 0 ${color}) drop-shadow(-${initialDistance}px 0 0 ${color}) drop-shadow(0 ${initialDistance}px 0 ${color}) drop-shadow(0 -${initialDistance}px 0 ${color})`;
          elem.style.filter = filterValue;
        }
        this.$set(selectedObject, 'outline', outline);
        this.imageBWidth           = outline.distance;
        this.tempImageOutlineColor = outline.color;
        EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      }, 10);
    },
    applyShadowOnImage() {
      this.getSelectedObjectsList.forEach((object) => {
        if (object.type === 'image') {
          this.applyShadowOnShapeById(object.Id);
        }
      });
      this.emitUndoRedoRequest('Image Shadow');
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange();
      this.emitUndoRedoRequest('applyShadowOnImage');
    },
    updateImageShadowProperty(property, value) {
      if (property === 'angle') {
        this.objectShadowAngle = value;
      }
      if (property === 'distance') {
        this.objectShadowDistance = value;
      }
      if (property === 'blur') {
        this.objectShadowBlur = value;
      }
      this.getSelectedObjectsList.forEach((object) => {
        if (object.type === 'image' || object.ImageType === 'SVGImage') {
          this.updateImageShadowPropertyById(property, value, object.Id);
        }
      });

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      this.emitUndoRedoRequest('From Image Shadow Effect');
    },
    updateImageShadowPropertyById(property, value, id) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject) {
        return;
      } // Ensure selectedObject exists

      const shadow = (selectedObject && selectedObject.shadow) ? selectedObject.shadow : {
        color:         '#846C63',
        X:             0,
        Y:             0,
        blur:          0,
        angle:         0,
        distance:      this.objectShadowDistance,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      };

      if (property === 'angle' || property === 'distance') {
        shadow.angle    = property === 'angle' ? value : shadow.angle;
        shadow.distance = property === 'distance' ? value : shadow.distance;

        let scaledDistance = shadow.distance * Math.max(selectedObject.Height, selectedObject.Width);
        scaledDistance     = selectedObject.type == 'image' && selectedObject.ImageType == 'WhiteboxLogo' ? scaledDistance / 10 : scaledDistance;

        const radians = (shadow.angle * Math.PI) / 180;

        shadow.shadowOffsetX = scaledDistance * Math.cos(radians);
        shadow.shadowOffsetY = scaledDistance * Math.sin(radians);
      } else {
        shadow[property] = property !== 'color' ? parseFloat(value) : value;
      }
      selectedObject.shadow = shadow;

      // apply shadow to the element;
      const elem = document.querySelector(`.canvas-main-html-container [id='${selectedObject.Id}']`);
      if (elem) {
        // Get computed filter value
        let currentFilter = getComputedStyle(elem).filter;

        // Normalize empty/none filter
        if (!currentFilter || currentFilter === 'none') {
          currentFilter = '';
        }

        // Remove existing drop-shadow if present
        const filterWithoutDropShadow = currentFilter
            .split(/\s+(?![^(]*\))/) // split by spaces not inside functions
            .filter(f => !f.startsWith('drop-shadow'))
            .join(' ');

        const newShadowValue = `drop-shadow(${shadow.shadowOffsetX}px ${shadow.shadowOffsetY}px ${shadow.blur * 40}px ${shadow.color})`;

        const updatedFilter = filterWithoutDropShadow ? `${filterWithoutDropShadow} ${newShadowValue}` : newShadowValue;

        // Apply the updated filter
        elem.style.filter = updatedFilter;
      }
    },
    updateImageBWidth(event) {
      this.imageBWidth = event.target.value;
      EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CHANGE_IMAGE_BORDER_WIDTH, this.imageBWidth);
    },
    updateImageBorderColor(color) {
      console.log('LEFT SECTION updateImageBorderColor');
      this.tempImageOutlineColor = color;
    },
    // LeftSection forwards CHANGE_SHAPE_SHADOW_COLOR here after it has updated the object and the canvas (R5),
    // so the open panel's shadow swatch shows the new colour too.
    applyShadowColorState(color, Id) {
      const selectedObject = this.contentList && findObjectById(this.contentList, Id);
      if (!selectedObject) {
        return;
      }
      this.tempShapeShadowColor = color;
      if (selectedObject.type == 'image') {
        this.tempImageShadowColor = color;
      }
    },
    onImageOutlineInput(payload) {
      this.updateImageBWidth({ target: { value: payload.value } });
    },
    onImageShadowInput(payload) {
      if (payload.key === 'angle') {
        this.updateImageShadowProperty('angle', parseInt(payload.value, 10));
      } else if (payload.key === 'distance') {
        this.updateImageShadowProperty('distance', parseFloat(payload.value));
      } else if (payload.key === 'blur') {
        this.updateImageShadowProperty('blur', payload.value);
      }
    },
    openImageEffectColorPicker(e) {
      if (e && e.stopPropagation) {
        e.stopPropagation();
      }
      const opening = !this.isColorPickerOpen;
      if (opening) {
        const trigger                = (e && (e.currentTarget || e.target)) || null;
        this.imageEffectColorPopover = this.sidePopoverPosition(trigger, { width: 300, height: 480 });
      }
      this.openColorPicker();
      if (opening) {
        this.$nextTick(() => this.fitImageEffectColorPopover());
      }
    },
    fitImageEffectColorPopover() {
      this.imageEffectColorPopover = this.fitSidePopover(
          this.$refs.imageEffectColorPopoverEl,
          this.imageEffectColorPopover,
      );
    },
  },
};
